// MisCharge.jsx
import { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Divider } from "@mui/material";
import FieldsTable from "@/Components/Tables/FieldsTable";
import { SectionHeader } from "@/Components/Header/SectionsHeader";
import { LuX } from "@/Components/Icons/Icons";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import InputTextField from "../../../Components/Form/InputTextField";
import { useUser } from "../../../Context/UserContext";
import {
  useGetMiscChargesByIdQuery,
  useUpsertMiscChargesMutation,
} from "../../../redux/ImportPage/CreateNewJob/Invoice/InvoiceMisc";
import SuccessDialog from "../../../Components/Dialog/SuccessDialog";
export default function MisCharge({ onClose }) {
  const [rows, setRows] = useState([]);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [subDialogConfig, setSubDialogConfig] = useState(false);
  const [totalOtherCharges, setTotalOtherCharges] = useState(0);
  const [totalInvoiceValue, setTotalInvoiceValue] = useState(0);
  const [saveEnabled, setSaveEnabled] = useState(false);


  const formData = useUser((s) => s.formData);
  const loading = useUser((s) => s.loading);
  const setLoading = useUser((s) => s.setLoading);
  const setFormData = useUser((s) => s.setFormData); // --------------------------------------------
  const setValueGlobal = useUser((s) => s.setValueGlobal); // ⭐ ADD
  const [totalProductValue, setTotalProductValue] = useState();
  useEffect(() => {
    const val = parseFloat(formData?.invoiceMain?.[0]?.importProductValue || 0);
    setTotalProductValue(val);
    setTotalInvoiceValue(val); // ⭐ DEFAULT SET
  }, [formData]);
  const importJobId = formData?.invoiceMain?.[0]?.importJobId;
  const importInvoiceId = formData?.invoiceMain?.[0]?.id;

  const { data, isLoading } = useGetMiscChargesByIdQuery({
    importJobId,
    importInvoiceId,
  });
  const [upsertMiscCharges] = useUpsertMiscChargesMutation();

  // FIXED MASTER LIST (MEMOIZED)
  const API_FIELD_MAP = [
    {
      apiCheck: "isBrokerageAndCommissions",
      apiRate: "brokerageAndCommissionsRate",
    },
    { apiCheck: "isCostOfContainer", apiRate: "costOfContainerRate" },
    { apiCheck: "isCostOfPacking", apiRate: "costOfPackingRate" },
    { apiCheck: "isHandlingCharges", apiRate: "handlingChargesRate" },
    {
      apiCheck: "isCostOfGoodsAndServices",
      apiRate: "costOfGoodsAndServicesRate",
    },
    { apiCheck: "isDocumentation", apiRate: "documentationRate" },
    { apiCheck: "isCountryOfOrigin", apiRate: "countryOfOriginRate" },
    {
      apiCheck: "isRoyaltiesAndLicenseFees",
      apiRate: "royaltiesAndLicenseFeesRate",
    },
    { apiCheck: "isProceedsAccrue", apiRate: "proceedsAccrueRate" },
    { apiCheck: "isCostOfWarranty", apiRate: "costOfWarrantyRate" },
    { apiCheck: "isOtherCost", apiRate: "otherCostRate" },
    { apiCheck: "isOtherCharges", apiRate: "otherChargesRate" },
    { apiCheck: "isLoadingCharges", apiRate: "loadingChargesRate" },
    { apiCheck: "isUnloadingCharges", apiRate: "unloadingChargesRate" },
  ];

  // --------------------------------------------
  const CHARGE_MASTER = useMemo(
    () => [
      "A - Brokerage and Commissions",
      "B - Cost of Container",
      "C - Cost of Packing",
      "D - Handling Charges",
      "E - Cost of Goods and Services",
      "F - Documentation",
      "G - Country of origin Certificate",
      "H - Royalties and Licence Fees",
      "I - Value of Proceeds Which Accrue",
      "J - Cost of Warranty Services",
      "K - Other Cost or Payment",
      "L - Other Charges and Payment",
      "M - Loading Charges",
      "N - Unloading Charges",
    ],
    []
  );

  // --------------------------------------------
  // INITIAL 14 STATIC ROWS (NO USEEFFECT)
  // --------------------------------------------
  const staticRows = useMemo(() => {
    return CHARGE_MASTER.map((label, i) => ({
      id: i + 1,
      editable: true,
      charges: { type: ["display"], value: [label] },
      checkbox: { type: ["checkbox"], value: [false] },

      // ⛔ Default disabled
      rate: {
        type: ["number"],
        value: [""],
        disabled: [true],
        dataType: "float",
      },
      amount: {
        type: ["number"],
        value: [""],
        disabled: [true],
        dataType: "float",
        editable: false,
      },
    }));
  }, [CHARGE_MASTER]);

  useEffect(() => {
    if (!data) return;

    const api = data; // backend result

    const updatedRows = staticRows.map((row, i) => {
      const checkKey = API_FIELD_MAP[i].apiCheck;
      const rateKey = API_FIELD_MAP[i].apiRate;

      const isChecked = api[checkKey];
      const rate = api[rateKey];

      const amount =
        isChecked && rate ? ((totalProductValue * rate) / 100).toFixed(2) : "";

      return {
        ...row,
        checkbox: { ...row.checkbox, value: [isChecked] },
        rate: {
          ...row.rate,
          disabled: [!isChecked],
          value: [isChecked ? rate : ""],
        },
        amount: { ...row.amount, value: [amount] },
      };
    });

    setRows(updatedRows);
    calculateTotals(updatedRows);
  }, [data, staticRows, totalProductValue]);

  // Load static rows ONLY once
  if (rows.length === 0) {
    setRows(staticRows);
  }
  const checkIfFormChanged = (updatedRows) => {
    const hasChange = updatedRows.some((r) => {
      const isChecked = r.checkbox.value[0];
      const rate = r.rate.value[0];
  
      return isChecked || (rate && rate !== "");
    });
  
    setSaveEnabled(hasChange);
  };
  

  // --------------------------------------------
  // HANDLE RATE CHANGE → AMOUNT UPDATE
  // --------------------------------------------
  const handleChange = useCallback(
    ({ rowId, key, value }) => {
      setRows((prev) => {
        const updatedRows = prev.map((r) => {
          if (r.id !== rowId) return r;
  
          let updated = {
            ...r,
            [key]: { ...r[key], value: [value] },
          };
  
          if (key === "checkbox") {
            const isChecked = value === true;
            updated.rate = {
              ...updated.rate,
              disabled: [!isChecked],
              value: isChecked ? updated.rate.value : [""],
            };
            updated.amount = {
              ...updated.amount,
              value: isChecked ? updated.amount.value : [""],
            };
          }
  
          if (key === "rate") {
            const rate = parseFloat(value || 0);
            const amount = ((totalProductValue * rate) / 100).toFixed(2);
            updated.amount = {
              ...updated.amount,
              value: [amount],
            };
          }
  
          return updated;
        });
  
        calculateTotals(updatedRows);
  
        // ⭐ ENABLE SAVE BUTTON
        checkIfFormChanged(updatedRows);
  
        return updatedRows;
      });
    },
    [totalProductValue]
  );
  
  // COLUMN SCHEMA
  // --------------------------------------------
  const columnFields = useMemo(
    () => [
      { key: "charges", label: "Charges", width: "300px" },
      { key: "checkbox", label: "", width: "30px" },
      { key: "rate", label: "Rate (%)", width: "120px" },
      { key: "amount", label: "Amount", width: "120px" },
    ],
    []
  );
  const buildChargesPayload = () => {
    const Charges = {};

    rows.forEach((row, i) => {
      const { apiCheck, apiRate } = API_FIELD_MAP[i];

      const isChecked = row.checkbox.value[0];
      const rate = parseFloat(row.rate.value[0] || 0);

      Charges[apiCheck] = isChecked;
      Charges[apiRate] = rate;
    });

    return Charges;
  };

  // --------------------------------------------
  // CALCULATE TOTAL OTHER CHARGES
  // --------------------------------------------
  const calculateTotals = useCallback(
    (currentRows) => {
      const otherCharges = currentRows.reduce((acc, r) => {
        const amt = parseFloat(r.amount.value[0] || 0);
        return acc + amt;
      }, 0);

      setTotalOtherCharges(otherCharges.toFixed(2));

      const invoice = (
        parseFloat(totalProductValue || 0) + otherCharges
      ).toFixed(2);
      setTotalInvoiceValue(invoice);
    },
    [totalProductValue]
  );


  const handleCloseAndSave = () => {
    const setValueGlobal = useUser.getState().setValueGlobal;
  
    // 🔥 Push to global form values also
    setValueGlobal("invoiceMain.0.totalOtherCharges", Number(totalOtherCharges));
    setValueGlobal("invoiceMain.0.totalInvoiceValue", Number(totalInvoiceValue));
  
    // 🔥 Also store in context (optional)
    setFormData((prev) => ({
      ...prev,
      invoiceMain: [
        {
          ...prev?.invoiceMain?.[0],
          totalOtherCharges: Number(totalOtherCharges),
          totalInvoiceValue: Number(totalInvoiceValue),
        },
      ],
    }));
  
    onClose();
  };
  
  // --------------------------------------------
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--gray-0)",
        width: "100%",
        height: 680,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingRight: 2,
        }}
      >
        <SectionHeader title={"Misc Charges"} />
        <LuX
          size={20}
          style={{ cursor: "pointer" }}
          onClick={handleCloseAndSave}
        />
      </Box>

      <Divider />

      {/* Table */}
      <FieldsTable
        rowData={rows}
        columnFields={columnFields}
        onChange={handleChange}
      />

      {/* Bottom Calculations */}
      <Box
        px={2}
        py={1}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {/* Row 1 */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <InputTextField
            label="Total Product Value"
            width="20%"
            height={30}
            placeholder="0.00"
            value={totalProductValue}
            dataType={"float"}
            onChange={(e) => {
              const val = parseFloat(e.target.value || 0);
              setTotalProductValue(val);

              setRows((prev) =>
                prev.map((r) => {
                  const rate = parseFloat(r.rate.value[0] || 0);
                  const amount = ((val * rate) / 100).toFixed(2);
                  return {
                    ...r,
                    amount: { ...r.amount, value: [amount] },
                  };
                })
              );

              calculateTotals();
            }}
          />

          <InputTextField
            label="Total Other Charges"
            width="20%"
            height={30}
            placeholder="0.00"
            value={totalOtherCharges}
            disabled
          />
        </Box>

        {/* Row 2 */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <InputTextField
            label="Total Invoice Value"
            width="20%"
            height={30}
            placeholder="0.00"
            value={totalInvoiceValue}
            disabled
          />
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          borderTop: "1px solid var(--gray-400)",
          px: 2,
          mt: 0,
          gap: 2,
        }}
      >
        <OutLinedButton
          label="Cancel"
          onClick={handleCloseAndSave}
          sx={{ width: 100, mt: 2.5 }}
        />
        <BlackButton
          label="Save"
          sx={{ width: 100, mt: 2.5 }}
          disabled={!saveEnabled}   // <<---- ADD THIS
          onClick={async () => {
            if (!saveEnabled) return;
            const Charges = buildChargesPayload();

            try {
              const response = await upsertMiscCharges({
                importJobId,
                importInvoiceId,
                Charges,
              }).unwrap();

              const success =
                response?.statusCode === 200 ||
                response?.data?.statusCode === 200;

              setSuccessDialogConfig({
                label: success ? "Success" : "Failure",
                title: "",
                subtitle: (
                  <strong
                    style={{ fontSize: "1rem", color: "var(--brand-900)" }}
                  >
                    {response?.message || "Data saved successfully"}
                  </strong>
                ),
                buttonText: "OK",
                status: success ? "success" : "error",
              });

              setShowSuccessDialog(true);
            } catch (err) {
              setSuccessDialogConfig({
                label: "Failure",
                title: "",
                subtitle: (
                  <strong
                    style={{ fontSize: "1rem", color: "var(--brand-900)" }}
                  >
                    {err?.data?.message || "Something went wrong"}
                  </strong>
                ),
                buttonText: "OK",
                status: "error",
              });

              setShowSuccessDialog(true);
            }

            handleCloseAndSave();
          }}
        />
        <SuccessDialog
          open={showSuccessDialog}
          onClose={() => {
            setShowSuccessDialog(false);
          }}
          {...successDialogConfig}
        />
      </Box>
    </Box>
  );
}
